import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import ConfirmModal from '../../components/ConfirmModal.jsx';

/**
 * Assistance Area Master (8951): the operational / tariff areas within each
 * port. Tariffs can differ by area even inside one port, so a booking names its
 * area and the service rate for that area applies.
 */

const EMPTY = { id: null, portId: '', name: '', code: '', description: '', active: true };

export default function AssistanceAreasPage() {
  const [ports, setPorts] = useState([]);
  const [areas, setAreas] = useState([]);
  const [portFilter, setPortFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const [portRows, areaRows] = await Promise.all([api.listPorts(), api.listAssistanceAreas()]);
    setPorts(portRows || []);
    setAreas(areaRows || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load assistance areas.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const shown = useMemo(() => areas.filter((a) => !portFilter || a.portId === portFilter), [areas, portFilter]);
  const countByPort = useMemo(() => {
    const m = new Map();
    for (const a of areas) m.set(a.portId, (m.get(a.portId) || 0) + 1);
    return m;
  }, [areas]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      delete next.form;
      return next;
    });
  };

  const openNew = () => {
    setErrors({});
    setForm({ ...EMPTY, portId: portFilter });
  };

  const openEdit = (a) => {
    setErrors({});
    setForm({
      id: a.id,
      portId: a.portId,
      name: a.name,
      code: a.code || '',
      description: a.description || '',
      active: a.active,
    });
  };

  const validate = () => {
    const next = {};
    if (!form.portId) next.portId = 'Select a port';
    if (!form.name.trim()) next.name = 'Area name is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e) => {
    e.preventDefault();
    setPageMsg(null);
    if (!validate()) return;
    setSaving(true);
    const payload = {
      portId: form.portId,
      name: form.name.trim(),
      code: form.code.trim() || null,
      description: form.description.trim() || null,
      active: form.active,
    };
    try {
      const saved = form.id
        ? await api.updateAssistanceArea(form.id, payload)
        : await api.createAssistanceArea(payload);
      setForm(null);
      setPageMsg({
        type: 'success',
        text: `Assistance area ${saved.name} ${form.id ? 'updated' : 'added'} for ${saved.portName}.`,
      });
      await load();
    } catch (err) {
      const fieldErrors = Object.fromEntries((err.details || []).map((d) => [d.path, d.message]));
      setErrors({ ...fieldErrors, form: err.message || 'Failed to save the assistance area.' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.deleteAssistanceArea(confirmDel.id);
      setPageMsg({ type: 'success', text: `Assistance area ${confirmDel.name} deleted.` });
      setConfirmDel(null);
      await load();
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to delete the assistance area.' });
      setConfirmDel(null);
    } finally {
      setDeleting(false);
    }
  };

  const fieldClass = (key, base = 'form-control form-control-sm') => `${base}${errors[key] ? ' is-invalid' : ''}`;

  return (
    <div className="container-fluid py-4" data-testid="assistance-areas-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-draw-polygon me-2" aria-hidden="true" />
                Assistance Areas
              </h3>
              <p className="text-muted mb-0 small">
                The operational and tariff areas of each port. Bookings name their area; tariffs can differ by area.
              </p>
            </div>
            <button type="button" className="btn btn-primary" onClick={openNew} data-testid="new-area">
              <i className="fas fa-plus me-1" aria-hidden="true" />
              New Assistance Area
            </button>
          </div>
        </div>
      </div>

      {loadError && <div className="alert alert-danger">{loadError}</div>}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="area-message">
          {pageMsg.text}
        </div>
      )}

      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white pt-3 border-bottom-0 d-flex flex-wrap gap-2 align-items-center">
          <label htmlFor="area-port-filter" className="small fw-bold text-muted mb-0">
            Port
          </label>
          <select
            id="area-port-filter"
            className="form-select form-select-sm w-auto"
            value={portFilter}
            onChange={(e) => setPortFilter(e.target.value)}
            data-testid="area-port-filter"
          >
            <option value="">All ports</option>
            {ports.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({countByPort.get(p.id) || 0})
              </option>
            ))}
          </select>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0" data-testid="areas-table">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <th>Port</th>
                    <th>Assistance area</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No assistance areas {portFilter ? 'for this port ' : ''}yet.
                      </td>
                    </tr>
                  )}
                  {shown.map((a) => (
                    <tr key={a.id} data-testid="area-row" data-name={a.name}>
                      <td>{a.portName}</td>
                      <td className="fw-bold">
                        {a.name}
                        {a.description && <small className="d-block text-muted fw-normal">{a.description}</small>}
                      </td>
                      <td>{a.code || '—'}</td>
                      <td>
                        <span className={`badge ${a.active ? 'bg-success' : 'bg-secondary'}`}>
                          {a.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => openEdit(a)}
                          data-testid="edit-area"
                        >
                          <i className="fas fa-pen me-1" aria-hidden="true" />
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setConfirmDel(a)}
                          data-testid="delete-area"
                        >
                          <i className="fas fa-trash me-1" aria-hidden="true" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {form && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="area-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={save} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-draw-polygon me-2 text-primary" aria-hidden="true" />
                      {form.id ? `Edit ${form.name || 'Assistance Area'}` : 'New Assistance Area'}
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setForm(null)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && (
                      <div className="alert alert-danger" role="alert" data-testid="area-error">
                        {errors.form}
                      </div>
                    )}
                    <div className="mb-2">
                      <label htmlFor="area-port" className="form-label small fw-bold text-muted">
                        Port <span className="text-danger">*</span>
                      </label>
                      <select
                        id="area-port"
                        className={fieldClass('portId', 'form-select form-select-sm')}
                        value={form.portId}
                        onChange={(e) => setField('portId', e.target.value)}
                        data-testid="area-port"
                      >
                        <option value="">Select a port…</option>
                        {ports.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      {errors.portId && <div className="invalid-feedback">{errors.portId}</div>}
                    </div>
                    <div className="row g-2">
                      <div className="col-8 mb-2">
                        <label htmlFor="area-name" className="form-label small fw-bold text-muted">
                          Area name <span className="text-danger">*</span>
                        </label>
                        <input
                          id="area-name"
                          className={fieldClass('name')}
                          maxLength={120}
                          value={form.name}
                          onChange={(e) => setField('name', e.target.value)}
                          data-testid="area-name"
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      </div>
                      <div className="col-4 mb-2">
                        <label htmlFor="area-code" className="form-label small fw-bold text-muted">
                          Code
                        </label>
                        <input
                          id="area-code"
                          className="form-control form-control-sm"
                          maxLength={32}
                          value={form.code}
                          onChange={(e) => setField('code', e.target.value)}
                          data-testid="area-code"
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label htmlFor="area-desc" className="form-label small fw-bold text-muted">
                        Description
                      </label>
                      <textarea
                        id="area-desc"
                        rows={2}
                        className="form-control form-control-sm"
                        maxLength={1000}
                        value={form.description}
                        onChange={(e) => setField('description', e.target.value)}
                        data-testid="area-description"
                      />
                    </div>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="area-active"
                        checked={form.active}
                        onChange={(e) => setField('active', e.target.checked)}
                        data-testid="area-active"
                      />
                      <label className="form-check-label small fw-bold text-muted" htmlFor="area-active">
                        Active — offered on new bookings
                      </label>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setForm(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-area">
                      {saving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
                      Save Area
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}

      <ConfirmModal
        open={Boolean(confirmDel)}
        title="Delete assistance area"
        message={
          confirmDel && (
            <>
              Delete <strong>{confirmDel.name}</strong> ({confirmDel.portName})? Its service rates go with it. An area that
              has bookings cannot be deleted — mark it inactive instead.
            </>
          )
        }
        busy={deleting}
        onConfirm={remove}
        onCancel={() => setConfirmDel(null)}
      />
    </div>
  );
}
